import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotification } from '../Contexts/NotificationContext';

const FeaturedsList = () => {
    const { setNotification } = useNotification();
    const [destaques, setDestaques] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const PER_PAGE = 8;

    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;
    const observer = useRef(null);

    const fetchCases = useCallback(async () => {
        if (!hasMore || loadingMore) return;

        setLoadingMore(true);
        try {
            const response = await fetch(`${baseUrl}/painel/destaques?page=${page}&per_page=${PER_PAGE}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                const newCases = data.destaques;

                setDestaques((prevCases) => [...prevCases, ...newCases]);
                setPage((prevPage) => prevPage + 1);

                if (newCases.length < PER_PAGE) {
                    setHasMore(false);
                }
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: 'Erro na requisição de conteúdo',
                show: true,
            });
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [hasMore, loadingMore, page, baseUrl, accessToken]);

    useEffect(() => {
        fetchCases();
    }, []);

    const lastCaseRef = useCallback(
        (node) => {
            if (loadingMore || loading) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    fetchCases();
                }
            });

            if (node) observer.current.observe(node);
        },
        [loadingMore, hasMore, fetchCases]
    );

    if (loading)
        return (
            <div className="grid grid-cols-4 gap-10 opacity-20">
                {[...Array(8)].map((_, index) => (
                    <div
                        key={index}
                        role="status"
                        className="flex items-center justify-center h-0 pb-[103%] max-w-sm bg-gray-300 rounded-lg animate-pulse dark:bg-gray-700"
                    >
                        <span className="sr-only">Loading...</span>
                    </div>
                ))}
            </div>
        );

    return (
        <div className="my-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
                {destaques.map((item, index) => (
                    <Link
                        ref={index === destaques.length - 1 ? lastCaseRef : null}
                        to={`/painel/news/destaques/${item.slug}`}
                        className="group block relative"
                        key={index}
                    >
                        <div className="relative h-0 pb-[80%] rounded-xl overflow-hidden border border-neutral-500">
                            <img
                                src={item.imagem}
                                alt={item.titulo}
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="lazy"
                            />

                            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent transition-all opacity-0 scale-y-125 group-hover:opacity-50 group-hover:scale-y-100" />
                        </div>

                        <h3 className="text-lg text-white font-semibold line-clamp-1 leading-tight mt-4 mb-2">{item.titulo}</h3>

                        <p className="text-sm text-gray-500 line-clamp-3 leading-tight mb-4">{item.previa}</p>

                        <div className="text-sm text-white font-medium flex items-center gap-2">
                            ver mais
                            <span className="mt-0.5 w-2 h-2 border-r border-t border-white font-bold transition-all ease-out rotate-45 group-hover:translate-x-[5px]" />
                        </div>
                    </Link>
                ))}
            </div>

            {loadingMore && (
                <div className="flex justify-center mt-8">
                    <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}
        </div>
    );
};

export default FeaturedsList;
